import { useId, type HTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import { HelpTip } from './HelpTip'

export interface FormGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Massimo di colonne; default 2 (2 da 480px di contenitore). 3 solo fuori dai dialog (da 720px). */
  columns?: 1 | 2 | 3 | 4 | undefined
  children: ReactNode
}

/**
 * Griglia dei campi: le colonne le decide la larghezza del contenitore (container query), non il viewport.
 * 1 colonna sotto 480px, 2 da 480px, 3 da 720px e 4 da 840px (se richieste, solo nelle pagine). Gap 16. `className="md-span-full"` sul figlio
 * lo porta a tutta larghezza (textarea, gruppi di checkbox, editor ripetuti, anteprime).
 */
export function FormGrid({ columns = 2, className, children, ...rest }: FormGridProps) {
  return (
    <div className="md-form-grid-host">
      <div {...rest} className={cx('md-form-grid', className)} data-columns={columns}>
        {children}
      </div>
    </div>
  )
}

export interface FormSectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: string | undefined
  description?: ReactNode | undefined
  columns?: 1 | 2 | 3 | 4 | undefined
  headingLevel?: 3 | 4 | undefined
  children: ReactNode
}

/** Sezione di un modulo: titolo title-small (un sostantivo), descrizione body-medium, campi in FormGrid. */
export function FormSection({ title, description, columns = 2, headingLevel = 3, className, children, ...rest }: FormSectionProps) {
  const id = useId()
  const Heading = `h${headingLevel}` as 'h3' | 'h4'
  return (
    <section {...rest} className={cx('md-form-section', className)} aria-labelledby={title ? id : undefined}>
      {title || description ? (
        <div className="md-form-section__head">
          {title ? <Heading id={id} className="md-form-section__title">{title}{description ? <HelpTip>{description}</HelpTip> : null}</Heading> : null}
          {description && !title ? <p className="md-form-section__desc">{description}</p> : null}
        </div>
      ) : null}
      <FormGrid columns={columns}>{children}</FormGrid>
    </section>
  )
}

export interface KeyValueListProps extends HTMLAttributes<HTMLDListElement> {
  items: { label: ReactNode; value: ReactNode; full?: boolean }[]
  /** 2 coppie per riga da 640px di contenitore. Default 1. */
  columns?: 1 | 2 | undefined
}

/**
 * Dettaglio in sola lettura (dl). Sotto 360px di contenitore l'etichetta sta sopra il valore;
 * sopra sono affiancati (40/60). I valori lunghi vanno a capo.
 */
export function KeyValueList({ items, columns = 1, className, ...rest }: KeyValueListProps) {
  return (
    <div className="md-kv-host">
      <dl {...rest} className={cx('md-kv', className)} data-columns={columns}>
        {items.map((it, i) => (
          <div key={i} className={cx('md-kv__item', it.full && 'md-kv__item--full')}>
            <dt className="md-kv__label">{it.label}</dt>
            <dd className="md-kv__value">{it.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
